<script>
import _ from 'lodash'

import TimelineItem from '../components/timeline/TimelineItem'
import PrintHeader from '../components/globals/print/PrintHeader'
import PrintButton from '../components/globals/print/PrintButton'
import EmptyState from '../components/globals/EmptyState'

import { createLabReport, deleteLabReport } from '../services/labReportService'
import { LAB_REPORTS_BY_YEAR } from '../graphql/queries/LabReportQueries'
import { ContentLoader } from 'vue-content-loader'
import { isMobile } from 'mobile-device-detect'

const PAGE_SIZE = 5

export default {
  name: 'TimelineView',
  components: {
    TimelineItem,
    ContentLoader,
    PrintHeader,
    PrintButton,
    EmptyState
  },
  data() {
    return {
      loading: false,
      page: 0,
      hasMore: true,
      labReports: [],
      isMobile: isMobile
    }
  },
  apollo: {
    labReports: {
      query: LAB_REPORTS_BY_YEAR,
      variables: {
        page: 0,
        pageSize: PAGE_SIZE
      },
      update: data => data.labReportsByYear,
      result() {
        this.loading = false
      }
    }
  },
  methods: {
    loadMore() {
      if (this.$apollo.loading || !this.hasMore) return
      this.page++

      this.$apollo.queries.labReports.fetchMore({
        variables: {
          page: this.page,
          pageSize: PAGE_SIZE
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          let reportsByYear = {}

          this.hasMore = !!fetchMoreResult.labReportsByYear.length

          const merge = x => {
            reportsByYear[x.year] = reportsByYear[x.year]
              ? {
                  reports: [...reportsByYear[x.year].reports, ...x.reports],
                  __typename: x.__typename
                }
              : { reports: x.reports, __typename: x.__typename }
          }

          previousResult.labReportsByYear.forEach(merge)
          fetchMoreResult.labReportsByYear.forEach(merge)

          const normalize = ({ reports, __typename }, year) => ({
            reports: reports.sort((a, b) => Number(b.date) - Number(a.date)),
            __typename,
            year
          })
          const sortByYear = (a, b) => Number(b.year) - Number(a.year)

          return {
            labReportsByYear: _.map(reportsByYear, normalize).sort(sortByYear)
          }
        }
      })
    },
    async deleteReport(id) {
      const oldReports = [...this.labReports]

      this.labReports = this.labReports.map(({ year, reports }) => ({
        year,
        reports: reports.filter(x => x._id !== id)
      }))
      const deletedReport = _.flatMap(oldReports, x => x.reports).find(
        x => x._id === id
      )

      try {
        await deleteLabReport(id)
        this.$amplitude.logEvent('Report Deleted', {
          id: id
        })
      } catch (error) {
        this.labReports = oldReports
        console.log(error)
      }

      this.$snackbar.open({
        duration: 5000,
        message: `Lab report deleted`,
        type: 'is-secondary',
        position: 'is-bottom-left',
        actionText: 'Undo',
        onAction: async () => {
          const { date, lab, results } = deletedReport
          const report = {
            date,
            lab,
            results: results.map(x => ({
              ...x,
              biomarker: x.biomarker.id
            }))
          }

          await createLabReport(report)
          this.labReports = oldReports
        }
      })
    }
  }
}
</script>

<template>
  <div class="timeline-view container section">
    <ContentLoader
      v-if="$apollo.loading"
      :height="150"
      :width="isMobile ? 200 : 400"
      :speed="3"
    >
      <rect x="0" y="0" rx="3" ry="3" width="155" height="12" />
      <rect x="0" y="20" rx="3" ry="3" width="350" height="4" />
      <rect x="0" y="30" rx="3" ry="3" width="380" height="4" />
      <rect x="0" y="40" rx="3" ry="3" width="201" height="4" />
    </ContentLoader>
    <PrintHeader v-if="!loading && labReports.length" />
    <PrintButton v-if="!loading && labReports.length" />
    <div v-if="!loading">
      <div v-if="labReports.length" class="timeline">
        <div
          v-for="report in labReports"
          :key="report.id"
          class="timeline-items"
        >
          <header class="timeline-header">
            <span class="tag is-tertiary">{{ report.year }}</span>
          </header>
          <div class="timeline-item">
            <div class="timeline-content">
              <div
                v-for="item in report.reports"
                :key="item.id"
                class="reports"
              >
                <TimelineItem :report="item" @delete="deleteReport" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyState
        v-else
        icon="fa-notes-medical"
        title="No lab reports"
        message="Add a report to start tracking your health."
      />
    </div>
    <div
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="!hasMore || loading"
      infinite-scroll-distance="200"
    />
  </div>
</template>

<style lang="sass" scoped>
.timeline
  width: calc(100% - 30px)

.timeline-header .tag
  font-size: 1rem
  height: 1.6em
  background-color: #bdccd4
  color: #004953
  width: 100%
  font-weight: 500

.timeline-items
  &:not(:last-child) > .timeline-item
    padding-bottom: 3em

.timeline-item
  width: 100%
  &::before
    background-color: #bdccd4
    width: 2px

.timeline-content
  width: 100%
  padding-left: 3em !important

@media (max-width: 768px)
  .timeline
    width: 100%
    .timeline-item
      margin-left: 0 !important
      &::before
        width: 0
      .timeline-content
        padding-left: 0 !important
</style>
